import { useEffect, useState } from 'react';
import { When } from 'react-if';
import styled from 'styled-components6';
import { isNotEmpty } from '@realwork/shared';
import { AnalyticsTargetLayerAreaProvider, Body1, JobMedia } from 'rwl-ui';
import { getJobDescription, serializeMedia } from '../../../utils';
import { PaneButtons } from './PaneButtons';
import { TargetLayerArea } from 'rwl-shared/dist/models/analytics';
import { useReview } from '../../../fetchers/ReviewFetcher';
import { useJob } from '../../../fetchers';
import { DetailsMode, useContentDetails } from '../../../providers';
import { DetailsReview } from '../DetailsReview';

const ContentContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'fullWidth', //remove console warning of invalid prop passed to HTML element
})<{ fullWidth?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: var(--rwl-spacing-lg) var(--rwl-spacing-md);
    gap: var(--rwl-spacing-md);
    background-color: var(--rwl-palette-gray-100);
    min-width: 224px;
    cursor: pointer;
    ${({ fullWidth }) =>
        fullWidth
            ? `
    > * {
        width: 100% !important; // For some reason this sometimes works/sometimes doesn't without !important
    }
    img {
        width: 100%;
        height: auto; // Ensures the image scales proportionally
        aspect-ratio: 1 / 1; // Locks aspect ratio to 1:1 (square)
        border-radius: var(--rwl-radius-sm);
    }
    `
            : ''}
    border-bottom-left-radius: var(--rwl-radius-sm);
    border-bottom-right-radius: var(--rwl-radius-sm);

    transition: height 0.3s ease; /* Smooth height transitions */
`;

export const Panes = () => {
    const [selectedPane, setSelectedPane] = useState<'description' | 'review'>(
        'description'
    );
    const { job } = useJob();
    const { review } = useReview();
    const { mode } = useContentDetails();

    useEffect(() => {
        if (mode === DetailsMode.JOB && job?.id) {
            setSelectedPane('description');
        }
    }, [job?.id, mode]);

    useEffect(() => {
        if (mode === DetailsMode.REVIEW && review?.id) {
            setSelectedPane('review');
        }
    }, [review?.id, mode]);

    return (
        <>
            <PaneButtons
                onClick={(pane) => setSelectedPane(pane)}
                selectedPane={selectedPane}
            />
            <When condition={selectedPane === 'description'}>
                <JobPane />
            </When>
            <When condition={selectedPane === 'review'}>
                <ReviewPane />
            </When>
        </>
    );
};

const JobPane = () => {
    const { job } = useJob();
    const { review } = useReview();
    if (!job) {
        return <></>;
    }
    const media = serializeMedia(job);
    const description = getJobDescription(job);

    return (
        <When condition={!!description || isNotEmpty(media)}>
            <ContentContainer
                style={{
                    marginTop: !review ? 'var(--rwl-spacing-md)' : 0,
                }}
                fullWidth={isNotEmpty(media)}
            >
                <When condition={isNotEmpty(media)}>
                    <JobMedia
                        media={media}
                        staticMapUrl={undefined}
                        variant="dark"
                    />
                </When>
                <When condition={!!description}>
                    <Body1>{description}</Body1>
                </When>
            </ContentContainer>
        </When>
    );
};

const ReviewPane = () => {
    const { review } = useReview();
    return review ? (
        <AnalyticsTargetLayerAreaProvider
            targetLayerArea={TargetLayerArea.REVIEWS}
        >
            <ContentContainer fullWidth={false}>
                <DetailsReview />
            </ContentContainer>
        </AnalyticsTargetLayerAreaProvider>
    ) : null;
};
